import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Link } from "@tiptap/extension-link";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import { Image } from "@tiptap/extension-image";
import { Bold, Palette, Link as LinkIcon, Unlink, Undo, Redo, Heading2, Heading3, List, ListOrdered, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";

const PRESET_COLORS = ["#0A1128", "#0F4C81", "#E11D48", "#059669", "#B91C1C", "#9333EA", "#EA580C", "#111827"];

export default function RichTextEditor({ value, onChange, testid, placeholder = "Add a description...", rich = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: rich ? { levels: [2, 3] } : false, codeBlock: false, blockquote: false, horizontalRule: false, link: false }),
      ...(rich ? [Image.configure({ HTMLAttributes: { class: "max-w-full border border-slate-200 my-2" } })] : []),
      TextStyle,
      Color,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "underline", target: "_blank", rel: "noopener noreferrer" } }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-32 p-3",
        "data-testid": testid ? `${testid}-editor` : "rte-editor",
        "data-placeholder": placeholder,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Treat truly empty content as empty string
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external `value` changes (e.g. loading existing content).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") && (value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Enter URL (leave blank to remove)", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    let normalized = url.trim();
    if (!/^https?:\/\//i.test(normalized) && !normalized.startsWith("mailto:")) {
      normalized = "https://" + normalized;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: normalized }).run();
  };

  const promptImage = () => {
    const url = window.prompt("Image or screenshot URL (https://...)");
    if (!url || !/^https?:\/\//i.test(url.trim())) return;
    editor.chain().focus().setImage({ src: url.trim(), alt: "" }).run();
  };

  const setColor = (c) => editor.chain().focus().setColor(c).run();
  const clearColor = () => editor.chain().focus().unsetColor().run();

  return (
    <div className="border border-slate-300 rounded-sm bg-white" data-testid={testid || "rte"}>
      <div className="flex items-center gap-0.5 border-b border-slate-200 bg-slate-50 p-1 flex-wrap">
        <ToolbarBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} testid={`${testid}-bold`} label="Bold">
          <Bold className="w-3.5 h-3.5"/>
        </ToolbarBtn>
        <div className="relative group">
          <ToolbarBtn onClick={() => {}} testid={`${testid}-color-open`} label="Text color">
            <Palette className="w-3.5 h-3.5"/>
          </ToolbarBtn>
          <div className="absolute left-0 top-8 z-20 hidden group-hover:flex hover:flex bg-white border-2 border-slate-300 p-2 gap-1 shadow-md" data-testid={`${testid}-color-menu`}>
            {PRESET_COLORS.map(c => (
              <button key={c} type="button" onClick={() => setColor(c)} className="w-5 h-5 rounded-sm border border-slate-300" style={{ background: c }} title={c} data-testid={`${testid}-color-${c.slice(1)}`}/>
            ))}
            <button type="button" onClick={clearColor} className="w-5 h-5 rounded-sm border border-slate-300 bg-white text-[10px] text-slate-500" title="Reset" data-testid={`${testid}-color-reset`}>×</button>
          </div>
        </div>
        {rich && <>
          <ToolbarBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} testid={`${testid}-h2`} label="Heading">
            <Heading2 className="w-3.5 h-3.5"/>
          </ToolbarBtn>
          <ToolbarBtn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} testid={`${testid}-h3`} label="Subheading">
            <Heading3 className="w-3.5 h-3.5"/>
          </ToolbarBtn>
          <ToolbarBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} testid={`${testid}-ul`} label="Bulleted list">
            <List className="w-3.5 h-3.5"/>
          </ToolbarBtn>
          <ToolbarBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} testid={`${testid}-ol`} label="Numbered steps">
            <ListOrdered className="w-3.5 h-3.5"/>
          </ToolbarBtn>
          <ToolbarBtn onClick={promptImage} testid={`${testid}-image`} label="Insert image / screenshot">
            <ImagePlus className="w-3.5 h-3.5"/>
          </ToolbarBtn>
        </>}
        <ToolbarBtn active={editor.isActive("link")} onClick={promptLink} testid={`${testid}-link`} label="Add link">
          <LinkIcon className="w-3.5 h-3.5"/>
        </ToolbarBtn>
        {editor.isActive("link") && (
          <ToolbarBtn onClick={() => editor.chain().focus().unsetLink().run()} testid={`${testid}-unlink`} label="Remove link">
            <Unlink className="w-3.5 h-3.5"/>
          </ToolbarBtn>
        )}
        <div className="ml-auto flex gap-0.5">
          <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} testid={`${testid}-undo`} label="Undo">
            <Undo className="w-3.5 h-3.5"/>
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} testid={`${testid}-redo`} label="Redo">
            <Redo className="w-3.5 h-3.5"/>
          </ToolbarBtn>
        </div>
      </div>
      <EditorContent editor={editor}/>
    </div>
  );
}

function ToolbarBtn({ active, onClick, children, testid, label }) {
  return (
    <Button type="button" size="sm" variant="ghost"
      className={`h-7 w-7 p-0 rounded-sm ${active ? "bg-[#0A1128] text-white hover:bg-slate-800 hover:text-white" : "text-slate-700 hover:bg-slate-200"}`}
      onClick={onClick} data-testid={testid} title={label} aria-label={label}>
      {children}
    </Button>
  );
}
